import { Text, View, type StyleProp, type ViewStyle } from "react-native";
import { AlertTriangle, CloudOff, RotateCcw } from "lucide-react-native";
import { CompactActionButton } from "./CompactActionButton";
import {
  GLASS_CARD_STYLE,
  colors,
  radii,
  semanticColors,
  spacing,
  typography,
} from "../theme/tokens";

const DARK_GLASS_CARD_STYLE: ViewStyle = {
  borderRadius: radii.lg,
  backgroundColor: "rgba(255, 255, 255, 0.04)",
  borderWidth: 1,
  borderColor: "rgba(255, 255, 255, 0.1)",
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.16,
  shadowRadius: 32,
};

type ClinicalRetryButtonProps = {
  retryLabel: string;
  retryingLabel: string;
  onRetry: () => void;
  isRetrying: boolean;
  dark: boolean;
  style?: StyleProp<ViewStyle>;
};

function ClinicalRetryButton({
  retryLabel,
  retryingLabel,
  onRetry,
  isRetrying,
  dark,
  style,
}: ClinicalRetryButtonProps) {
  const label = isRetrying ? retryingLabel : retryLabel;
  const contentColor = dark ? colors.warning : colors.text;

  return (
    <CompactActionButton
      label={label}
      accessibilityLabel={label}
      onPress={onRetry}
      loading={isRetrying}
      size="md"
      variant={dark ? "secondary" : "selected"}
      leading={<RotateCcw size={13} color={contentColor} strokeWidth={1.9} />}
      activityIndicatorColor={contentColor}
      pressedOpacity={dark ? 0.7 : undefined}
      style={[
        dark
          ? {
              backgroundColor: "rgba(255, 255, 255, 0.08)",
              borderColor: "rgba(255, 255, 255, 0.16)",
            }
          : null,
        style,
      ]}
      textStyle={dark ? { color: contentColor } : undefined}
    />
  );
}

export type ClinicalDataStatusProps = {
  state: "unavailable" | "stale";
  title?: string;
  message: string;
  retryLabel: string;
  retryingLabel: string;
  onRetry: () => void;
  isRetrying?: boolean;
  appearance?: "light" | "dark";
  /** Removes card chrome when the status is already inside a glass/card surface. */
  embedded?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/** Shared unavailable/stale-with-warning treatment for clinical records. */
export function ClinicalDataStatus({
  state,
  title,
  message,
  retryLabel,
  retryingLabel,
  onRetry,
  isRetrying = false,
  appearance = "light",
  embedded = false,
  style,
  testID,
}: ClinicalDataStatusProps) {
  const dark = appearance === "dark";
  const accent = dark ? colors.warning : colors.text;
  const primaryText = dark ? colors.warmLight : semanticColors.text.primary;
  const secondaryText = dark ? colors.warmLight : semanticColors.text.tertiary;
  const glassCardStyle = dark
    ? DARK_GLASS_CARD_STYLE
    : (GLASS_CARD_STYLE as ViewStyle);
  const Icon = state === "unavailable" ? CloudOff : AlertTriangle;

  if (state === "stale") {
    return (
      <View
        testID={testID}
        accessibilityLiveRegion="polite"
        style={[
          glassCardStyle,
          {
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.sm,
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.sm,
          },
          style,
        ]}
      >
        <Icon size={16} color={accent} strokeWidth={1.8} />
        <Text
          style={{
            flex: 1,
            fontFamily: typography.families.sans,
            fontSize: 12,
            lineHeight: 17,
            color: secondaryText,
            opacity: dark ? 0.76 : 1,
          }}
        >
          {message}
        </Text>
        <ClinicalRetryButton
          retryLabel={retryLabel}
          retryingLabel={retryingLabel}
          onRetry={onRetry}
          isRetrying={isRetrying}
          dark={dark}
        />
      </View>
    );
  }

  return (
    <View
      testID={testID}
      accessibilityLiveRegion="polite"
      style={[
        embedded ? null : glassCardStyle,
        {
          alignItems: "center",
          paddingHorizontal: spacing.xl,
          paddingVertical: spacing["3xl"],
        },
        style,
      ]}
    >
      <View
        style={{
          width: 52,
          height: 52,
          borderRadius: 26,
          alignItems: "center",
          justifyContent: "center",
          marginBottom: spacing.md,
          backgroundColor: dark
            ? "rgba(251, 191, 36, 0.1)"
            : "rgba(42, 37, 34, 0.1)",
        }}
      >
        <CloudOff size={23} color={accent} strokeWidth={1.6} />
      </View>
      {title ? (
        <Text
          style={{
            fontFamily: typography.families.sansMedium,
            fontSize: 16,
            lineHeight: 21,
            color: primaryText,
            textAlign: "center",
          }}
        >
          {title}
        </Text>
      ) : null}
      <Text
        style={{
          maxWidth: 360,
          marginTop: title ? spacing.sm : 0,
          fontFamily: typography.families.sans,
          fontSize: 13,
          lineHeight: 19,
          color: secondaryText,
          textAlign: "center",
        }}
      >
        {message}
      </Text>
      <ClinicalRetryButton
        retryLabel={retryLabel}
        retryingLabel={retryingLabel}
        onRetry={onRetry}
        isRetrying={isRetrying}
        dark={dark}
        style={{ marginTop: spacing.lg }}
      />
    </View>
  );
}
